// src/components/listing/ShareSheet.js
// BottomSheet de partage - natif mobile + WhatsApp/Facebook/copie lien - NE PAS MODIFIER

import React, { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';

const ShareSheet = ({ open, onClose, title, url, imageUrl }) => {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const shareText = `${title} - Découvrez cette annonce sur Fynho`;

  // Partage natif du téléphone (iOS/Android) si disponible, sinon on affiche les options manuelles
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url });
        onClose();
      } catch (err) {
        // L'utilisateur a annulé, on ne fait rien
      }
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`${shareText}\n\n${url}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
    onClose();
  };

  const handleFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
    onClose();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1200);
    } catch (err) {
      // Repli pour navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1200);
    }
  };

  const styles = {
    backdrop: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 2000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      animation: 'fadeInShare 200ms ease-out',
    },
    sheet: {
      width: '100%', maxWidth: '480px', backgroundColor: theme.bgCard,
      borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
      padding: '12px 20px calc(24px + env(safe-area-inset-bottom, 0px))',
      display: 'flex', flexDirection: 'column', gap: '14px',
      animation: 'slideUpShare 250ms ease-out', borderTop: `1px solid ${theme.border}`,
    },
    handle: { width: '40px', height: '4px', borderRadius: '2px', backgroundColor: theme.textMuted, margin: '4px auto 4px' },
    title: { fontSize: '17px', fontWeight: '700', color: theme.textPrimary, textAlign: 'center' },
    preview: {
      display: 'flex', gap: '10px', padding: '10px',
      backgroundColor: theme.bgInput, borderRadius: '10px', border: `1px solid ${theme.border}`,
    },
    previewImg: { width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
    previewText: { fontSize: '13px', color: theme.textPrimary, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
    optionsGrid: { display: 'flex', gap: '10px' },
    optionBtn: {
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
      padding: '14px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    },
    optionIcon: { fontSize: '24px' },
    whatsappOpt: { backgroundColor: 'rgba(37, 211, 102, 0.12)', color: '#25D366' },
    facebookOpt: { backgroundColor: 'rgba(24, 119, 242, 0.12)', color: '#1877F2' },
    copyOpt: { backgroundColor: theme.goldSoft, color: theme.gold },
    nativeShareBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput, color: theme.textPrimary, fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    },
    cancelBtn: { background: 'none', border: 'none', color: theme.textSecondary, fontSize: '15px', fontWeight: '600', cursor: 'pointer', padding: '8px' },
    copiedBadge: { textAlign: 'center', fontSize: '13px', color: theme.gold, fontWeight: '600' },
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <style>{`
        @keyframes fadeInShare { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpShare { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <div style={styles.title}>Partager</div>

        {imageUrl && (
          <div style={styles.preview}>
            <img src={imageUrl} alt="" style={styles.previewImg} />
            <div style={styles.previewText}>{title}</div>
          </div>
        )}

        {copied ? (
          <div style={styles.copiedBadge}>✓ Lien copié !</div>
        ) : (
          <>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button style={styles.nativeShareBtn} onClick={handleNativeShare}>
                📤 Plus d'options de partage
              </button>
            )}

            <div style={styles.optionsGrid}>
              <button style={{ ...styles.optionBtn, ...styles.whatsappOpt }} onClick={handleWhatsApp}>
                <span style={styles.optionIcon}>💬</span>
                WhatsApp
              </button>
              <button style={{ ...styles.optionBtn, ...styles.facebookOpt }} onClick={handleFacebook}>
                <span style={styles.optionIcon}>📘</span>
                Facebook
              </button>
              <button style={{ ...styles.optionBtn, ...styles.copyOpt }} onClick={handleCopyLink}>
                <span style={styles.optionIcon}>🔗</span>
                Copier
              </button>
            </div>
          </>
        )}

        <button style={styles.cancelBtn} onClick={onClose}>
          {copied ? '' : 'Annuler'}
        </button>
      </div>
    </div>
  );
};

export default ShareSheet;